// Colour, material and heel attributes affect order only. Type and purchasability are always required.
const normaliseOosAttribute = (value = '') => value.trim().toLowerCase().replace(/[\s_-]+/g, ' ');
const lightOosColours = new Set(['white', 'off white', 'cream', 'ivory']);
const quoteOosSearch = (value) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
const OOS_LIMIT = 6;
const OOS_MAX_PAGES = 3; // At most 150 small metadata records, then one request for cards.

function parseOosHeelTypes(value) {
	try {
		const types = JSON.parse(value || '[]');
		return Array.isArray(types) ? types.filter((type) => typeof type === 'string').map(normaliseOosAttribute).filter(Boolean) : [];
	} catch {
		return [];
	}
}

function rankOosCandidates(candidates, source) {
	const productType = normaliseOosAttribute(source.productType);
	const colour = normaliseOosAttribute(source.colour);
	const material = normaliseOosAttribute(source.material);
	const heelTypes = new Set(parseOosHeelTypes(source.heelTypes));
	const heelHeight = normaliseOosAttribute(source.heelHeight);
	const excludedIds = new Set((source.excludedIds || '').split(',').map((id) => id.trim()));
	const unique = new Map();

	for (const candidate of candidates) {
		if (!productType || normaliseOosAttribute(candidate.productType) !== productType ||
			candidate.available !== 'true' || excludedIds.has(candidate.id) || unique.has(candidate.id)) continue;

		const candidateColour = normaliseOosAttribute(candidate.colour);
		const candidateMaterial = normaliseOosAttribute(candidate.material);
		const candidateHeelHeight = normaliseOosAttribute(candidate.heelHeight);
		const sizeCount = Number(candidate.availableSizeCount);
		const availableSizeCount = Number.isInteger(sizeCount) && sizeCount > 0 ? sizeCount : 0;
		let score = 0;

		if (colour && colour === candidateColour) {
			score += 4;
		} else if (lightOosColours.has(colour) && lightOosColours.has(candidateColour)) {
			score += 3;
		} else if ((colour === 'beige' && lightOosColours.has(candidateColour)) ||
			(candidateColour === 'beige' && lightOosColours.has(colour))) {
			// Beige is a weaker match than two shades in the light-colour group.
			score += 2;
		}
		// A material match outranks a colour-only match without excluding other materials.
		if (material && material === candidateMaterial) score += 5;
		if (heelTypes.size && parseOosHeelTypes(candidate.heelTypes).some((type) => heelTypes.has(type))) score += 3;
		if (heelHeight && heelHeight === candidateHeelHeight) score += 3;
		unique.set(candidate.id, { ...candidate, score, availableSizeCount });
	}

	// Size coverage breaks relevance ties; otherwise retain Shopify's result order.
	return [...unique.values()].sort((a, b) => b.score - a.score || b.availableSizeCount - a.availableSizeCount);
}

class ProductOosState extends HTMLElement {
	connectedCallback() {
		this.rerenderScope = this.closest('product-rerender');
		if (!this.rerenderScope) return;
		this.listenerController?.abort();
		this.listenerController = new AbortController();
		// The native event does not bubble. Capture on its scope without changing the form's handlers.
		this.rerenderScope.addEventListener('product:rerender', (event) => this.onRerender(event), {
			capture: true, signal: this.listenerController.signal
		});
	}

	disconnectedCallback() {
		this.listenerController?.abort();
	}

	onRerender(event) {
		const scope = this.rerenderScope;
		if (event.target.getAttribute('id') !== scope.getAttribute('observe-form') || !event.detail?.htmlFragment ||
			event.detail.productChange || !scope.hasAttribute('allow-partial-rerender')) return;

		const incomingScope = [...event.detail.htmlFragment.querySelectorAll('product-rerender')]
			.find((element) => element.id === scope.id);
		const incoming = [...(incomingScope?.querySelectorAll('product-oos-state') || [])]
			.find((element) => element.dataset.blockId === this.dataset.blockId);
		if (!incoming || this.hasSameContext(incoming)) return;

		// Replace only this independent block when stock or recommendation inputs actually change.
		this.replaceWith(incoming);
	}

	hasSameContext(incoming) {
		if (!this.dataset.productId || this.dataset.productId !== incoming.dataset.productId ||
			this.dataset.productAvailable !== incoming.dataset.productAvailable) return false;

		const currentRecommendations = this.querySelector('oos-recommendations');
		const incomingRecommendations = incoming.querySelector('oos-recommendations');
		if (!currentRecommendations || !incomingRecommendations) return currentRecommendations === incomingRecommendations;

		// Compare server inputs only; loaded/resolved attributes belong to the live instance.
		return ['searchUrl', 'productType', 'colour', 'material', 'heelTypes', 'heelHeight', 'excludedIds']
			.every((key) => currentRecommendations.dataset[key] === incomingRecommendations.dataset[key]);
	}
}

class OosRecommendations extends HTMLElement {
	connectedCallback() {
		if (!this.isConnected || this.loaded || this.controller || this.observer || !this.dataset.productType?.trim()) return;

		// The recommendations stay hidden until populated; their wrapper retains layout for observation.
		const target = this.closest('.product-oos-state');
		if (!target || !('IntersectionObserver' in window)) {
			this.startLoading();
			return;
		}

		this.observer = new IntersectionObserver((entries, observer) => {
			if (this.observer !== observer || !entries.some((entry) => entry.isIntersecting)) return;
			this.startLoading();
		}, { rootMargin: '0px 0px 400px 0px' });
		this.observer.observe(target);
	}

	disconnectedCallback() {
		this.observer?.disconnect();
		this.observer = null;
		this.controller?.abort();
		this.controller = null;
	}

	startLoading() {
		this.observer?.disconnect();
		this.observer = null;
		if (!this.isConnected || this.loaded || this.controller) return;
		this.controller = new AbortController();
		this.load(this.controller.signal);
	}

	searchUrl(sectionId, query) {
		const url = new URL(this.dataset.searchUrl, window.location.origin);
		url.searchParams.set('q', query);
		url.searchParams.set('type', 'product');
		url.searchParams.set('options[unavailable_products]', 'hide');
		url.searchParams.set('filter.p.product_type', this.dataset.productType);
		url.searchParams.set('sort_by', 'relevance');
		url.searchParams.set('section_id', sectionId);

		// Keep an explicit theme preview when present; localized search paths come from Liquid.
		const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
		if (preview) url.searchParams.set('preview_theme_id', preview);
		return url;
	}

	async fetchSection(url, selector, signal) {
		const response = await fetch(url, { signal });
		if (!response.ok) throw new Error('OOS search request failed');
		const html = new DOMParser().parseFromString(await response.text(), 'text/html');
		const section = html.querySelector(selector);
		if (!section) throw new Error('OOS search section is missing');
		return section;
	}

	async load(signal) {
		const controller = this.controller;
		const timeout = setTimeout(() => controller.abort(), 20000);

		try {
			const query = `product_type:${quoteOosSearch(this.dataset.productType)}`;
			let url = this.searchUrl('oos-search-data', query);
			const candidates = [];

			for (let page = 0; url && page < OOS_MAX_PAGES; page++) {
				const results = await this.fetchSection(url, '[data-oos-results]', signal);
				candidates.push(...Array.from(results.querySelectorAll('[data-oos-candidate]'), (item) => ({ ...item.dataset })));
				const nextUrl = results.dataset.nextUrl;
				url = nextUrl ? new URL(nextUrl, url) : null;
				if (url && url.origin !== window.location.origin) throw new Error('Unexpected search pagination URL');
				if (url) url.searchParams.set('section_id', 'oos-search-data');
			}

			const selected = rankOosCandidates(candidates, this.dataset).slice(0, OOS_LIMIT);
			if (!selected.length) return;

			const cardQuery = `(${selected.map((item) => `title:${quoteOosSearch(item.title)}`).join(' OR ')}) AND ${query}`;
			const cards = await this.fetchSection(this.searchUrl('oos-search-cards', cardQuery), '[data-oos-cards]', signal);
			const fragment = document.createDocumentFragment();

			for (const candidate of selected) {
				const template = [...cards.querySelectorAll('template[data-oos-card]')]
					.find((item) => item.dataset.oosCard === candidate.id && item.dataset.productType === this.dataset.productType);
				if (template) fragment.append(template.content.cloneNode(true));
			}

			if (signal.aborted || !this.isConnected || !fragment.childElementCount) return;
			// Populate the inert template before the theme initializes its carousel.
			const layout = this.querySelector('[data-oos-layout]').content.cloneNode(true);
			layout.querySelector('[data-oos-list]').replaceChildren(fragment);
			this.append(layout);
			this.loaded = true;
			this.hidden = false;
			this.setAttribute('data-loaded', '');
		} catch (error) {
			// Keep the alternatives block hidden when search fails.
			if (error.name !== 'AbortError') console.warn('Could not load product alternatives.', error);
		} finally {
			clearTimeout(timeout);
			if (this.controller === controller) {
				this.controller = null;
				// Reveal the fallback after empty results, errors or timeout, never during a pending search.
				if (this.isConnected) this.setAttribute('data-resolved', '');
			}
		}
	}
}

if (!customElements.get('product-oos-state')) {
	customElements.define('product-oos-state', ProductOosState);
}

if (!customElements.get('oos-recommendations')) {
	customElements.define('oos-recommendations', OosRecommendations);
}
